import { useSyncExternalStore } from "react";
import { useSessionContext, type SessionContext } from "./session";

// The company the user is working in (LN: the company you log into). It is a
// per-user, per-organization UI default — which warehouses a screen opens
// with, which company a new document belongs to. It is not a security
// boundary: roles are tenant-wide (ADR-013) and RLS filters by tenant.
// Remembered in localStorage per organization; falls back to the first
// active company (the one created first) when nothing (valid) is stored.

type Company = SessionContext["companies"][number];

const listeners = new Set<() => void>();
const keyFor = (tenantId: string) => `patronix.company.${tenantId}`;

function read(tenantId: string): string | null {
  try {
    return localStorage.getItem(keyFor(tenantId));
  } catch {
    return null;
  }
}

export function setActiveCompany(tenantId: string, companyId: string): void {
  try {
    localStorage.setItem(keyFor(tenantId), companyId);
  } catch {
    // Storage unavailable (private mode): the choice lasts until reload.
    memory.set(tenantId, companyId);
  }
  listeners.forEach((l) => l());
}

const memory = new Map<string, string>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Active company outside React (route loaders): same resolution as the hook. */
export function activeCompanyOf(ctx: SessionContext): Company | undefined {
  const tenantId = ctx.tenant?.id ?? "";
  return resolveActiveCompany(ctx.companies, tenantId ? (memory.get(tenantId) ?? read(tenantId)) : null);
}

/** Pure resolution, exported for tests. */
export function resolveActiveCompany(companies: Company[], storedId: string | null): Company | undefined {
  return companies.find((c) => c.id === storedId) ?? companies[0];
}

export function useActiveCompany(): { company: Company | undefined; companies: Company[]; select: (id: string) => void } {
  const { data: ctx } = useSessionContext();
  const tenantId = ctx?.tenant?.id ?? "";
  const storedId = useSyncExternalStore(
    subscribe,
    () => (tenantId ? (memory.get(tenantId) ?? read(tenantId)) : null),
    () => null,
  );
  const companies = ctx?.companies ?? [];
  return {
    company: resolveActiveCompany(companies, storedId),
    companies,
    select: (id: string) => tenantId && setActiveCompany(tenantId, id),
  };
}
